/**
 * Fake statistics data for populating charts and metrics
 * This provides baseline data that makes the UI look populated,
 * and real database values are added on top to show actual user activity.
 */

/**
 * Hardcoded static fake calculation counts for each month
 * These values are around 50,000 with realistic seasonal variation
 * Real database values will be added on top of these baseline values
 */
const FAKE_MONTHLY_CALCULATION_COUNTS = [
  47800, // 12 months ago - lower (post-holiday)
  49200, // 11 months ago
  51500, // 10 months ago - rising
  53200, // 9 months ago - spring peak
  54100, // 8 months ago - high season
  52800, // 7 months ago
  51000, // 6 months ago - mid year
  49500, // 5 months ago - summer dip
  50800, // 4 months ago - autumn start
  52500, // 3 months ago - rising
  53800, // 2 months ago - peak season
  48900, // 1 month ago - current month (partial)
]

/**
 * Generate 12 months of fake monthly volume data
 * Returns months in YYYY-MM format with static realistic calculation counts
 * Base value around 50,000 analyses monthly for Erlang pool calculations
 */
export function getFakeMonthlyVolumes(): Array<{
  month: string
  calculation_count: number
}> {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1 // JavaScript months are 0-indexed

  const volumes: Array<{ month: string; calculation_count: number }> = []

  // Generate data for the last 12 months with static values
  for (let i = 11; i >= 0; i--) {
    const monthsAgo = i
    const targetDate = new Date(currentYear, currentMonth - 1 - monthsAgo, 1)
    const year = targetDate.getFullYear()
    const month = targetDate.getMonth() + 1
    const monthString = `${year}-${String(month).padStart(2, "0")}`

    // Use hardcoded static value for this month position
    const calculationCount = FAKE_MONTHLY_CALCULATION_COUNTS[11 - i]

    volumes.push({
      month: monthString,
      calculation_count: calculationCount,
    })
  }

  return volumes
}

/**
 * Fake average metrics for the statistics panel
 */
export const FAKE_AVERAGE_METRICS = {
  avg_handle_time: 340, // seconds (~5.6 minutes)
  avg_service_level: 72.5, // percentage
  avg_answer_time: 28, // seconds
  avg_shrinkage: 30.2, // percentage
  avg_occupancy: 85.7, // percentage
}

/**
 * Fake AHT distribution data
 * Follows a realistic bell curve pattern with peak around 3-5 minutes
 * Static hardcoded values - real database counts will be added on top
 */
export const FAKE_AHT_DISTRIBUTION: Array<{
  range_key: string
  percentage: number
  count: number
}> = [
  {
    range_key: "under1",
    percentage: 2.5,
    count: 12500, // 2.5% of ~50k calculations
  },
  {
    range_key: "1to2",
    percentage: 8.3,
    count: 41500, // 8.3% of ~50k calculations
  },
  {
    range_key: "2to3",
    percentage: 14.7,
    count: 73500, // 14.7% of ~50k calculations
  },
  {
    range_key: "3to4",
    percentage: 18.2,
    count: 91000, // 18.2% of ~50k calculations
  },
  {
    range_key: "4to5",
    percentage: 17.5,
    count: 87500, // 17.5% of ~50k calculations
  },
  {
    range_key: "5to6",
    percentage: 13.8,
    count: 69000, // 13.8% of ~50k calculations
  },
  {
    range_key: "6to8",
    percentage: 11.2,
    count: 56000, // 11.2% of ~50k calculations
  },
  {
    range_key: "8to10",
    percentage: 7.4,
    count: 37000, // 7.4% of ~50k calculations
  },
  {
    range_key: "10to15",
    percentage: 4.6,
    count: 23000, // 4.6% of ~50k calculations
  },
  {
    range_key: "15to20",
    percentage: 1.3,
    count: 6500, // 1.3% of ~50k calculations
  },
  {
    range_key: "20plus",
    percentage: 0.5,
    count: 2500, // 0.5% of ~50k calculations
  },
]

/**
 * Complete fake statistics object matching the database view structure
 */
export const FAKE_STATISTICS = {
  monthly_volumes: getFakeMonthlyVolumes(),
  average_metrics: FAKE_AVERAGE_METRICS,
  aht_distribution: FAKE_AHT_DISTRIBUTION,
}

/**
 * Merge fake monthly volumes with real data
 * Real calculation counts are added on top of fake baseline
 */
export function mergeMonthlyVolumes(
  fakeVolumes: Array<{ month: string; calculation_count: number }>,
  realVolumes: Array<{ month: string; calculation_count: number }> | null,
): Array<{ month: string; calculation_count: number }> {
  if (!realVolumes || realVolumes.length === 0) {
    return fakeVolumes
  }

  const realVolumeMap = new Map(realVolumes.map((v) => [v.month, v.calculation_count]))

  return fakeVolumes.map((fakeVolume) => {
    const realCount = realVolumeMap.get(fakeVolume.month) || 0
    return {
      month: fakeVolume.month,
      calculation_count: fakeVolume.calculation_count + realCount,
    }
  })
}

/**
 * Merge fake average metrics with real data
 * Real values take precedence when available
 */
export function mergeAverageMetrics(
  fakeMetrics: typeof FAKE_AVERAGE_METRICS,
  realMetrics: {
    avg_handle_time: number | null
    avg_service_level: number | null
    avg_answer_time: number | null
    avg_shrinkage: number | null
    avg_occupancy: number | null
  } | null,
): typeof FAKE_AVERAGE_METRICS {
  if (!realMetrics) {
    return fakeMetrics
  }

  return {
    avg_handle_time: realMetrics.avg_handle_time ?? fakeMetrics.avg_handle_time,
    avg_service_level: realMetrics.avg_service_level ?? fakeMetrics.avg_service_level,
    avg_answer_time: realMetrics.avg_answer_time ?? fakeMetrics.avg_answer_time,
    avg_shrinkage: realMetrics.avg_shrinkage ?? fakeMetrics.avg_shrinkage,
    avg_occupancy: realMetrics.avg_occupancy ?? fakeMetrics.avg_occupancy,
  }
}

/**
 * Merge fake AHT distribution with real data
 * Real percentages and counts are added on top of fake baseline
 */
export function mergeAHTDistribution(
  fakeDistribution: typeof FAKE_AHT_DISTRIBUTION,
  realDistribution: Array<{
    range_key: string
    percentage: number
    count: number
  }> | null,
): typeof FAKE_AHT_DISTRIBUTION {
  if (!realDistribution || realDistribution.length === 0) {
    return fakeDistribution
  }

  const realDistMap = new Map(
    realDistribution.map((d) => [d.range_key, { percentage: d.percentage, count: d.count }]),
  )

  // Add real data on top of fake, then recalculate percentages
  const merged = fakeDistribution.map((fakeItem) => {
    const realItem = realDistMap.get(fakeItem.range_key)
    if (realItem) {
      return {
        range_key: fakeItem.range_key,
        count: fakeItem.count + realItem.count,
        percentage: 0, // Will recalculate below
      }
    }
    return { ...fakeItem }
  })

  // Recalculate percentages based on total count
  const totalCount = merged.reduce((sum, item) => sum + item.count, 0)
  return merged.map((item) => ({
    ...item,
    percentage: totalCount > 0 ? Math.round((item.count / totalCount) * 1000) / 10 : 0,
  }))
}
